import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence, useDragControls } from 'framer-motion';
import QRCode from 'react-qr-code';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';
import { documentsAPI, shopkeepersAPI, TokenManager, API_CONFIG } from '../api';
import useWebSocket from '../hooks/useWebSocket';
import type { Document, DocumentGroup, Sender, Shopkeeper, NotificationMessage } from '../types';
import OpenClosedToggle from '../components/OpenClosedToggle';
import DocumentViewer from '../components/DocumentViewer';
import { useAuth } from '../context/AuthContext';
import Toast, { type ToastProps } from '../components/Toast';
import {
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  MagnifyingGlassIcon,
  DocumentIcon,
  SpeakerWaveIcon,
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  PrinterIcon
} from '@heroicons/react/24/outline';

const ModernInboxDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  // State management
  const [profile, setProfile] = useState<Shopkeeper | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSender, setSelectedSender] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState<'all' | 'unread' | 'printed' | 'pending'>('all');
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);

  // Sync filter with bottom navigation / URL query params
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'printed') {
      setFilter('printed');
    } else if (tab === 'inbox') {
      setFilter('all');
    }
  }, [searchParams]);
  const [toasts, setToasts] = useState<Array<ToastProps & { id: number }>>([]);
  const [newDocId, setNewDocId] = useState<number | null>(null);
  const toastIdRef = useRef(0);
  const documentViewerRef = useRef<HTMLDivElement>(null);

  // WebSocket connection for real-time updates
  // Get WebSocket URL from environment variable or default to localhost
  const getWebSocketUrl = () => {
    if (!profile) return null;

    const apiBaseUrl = API_CONFIG.baseURL;
    // Convert HTTP(S) URL to WS(S) URL
    // Railway serves WebSocket on the same URL/port as HTTP
    const wsBaseUrl = apiBaseUrl
      .replace('https://', 'wss://')
      .replace('http://', 'ws://');

    return `${wsBaseUrl}/ws/shopkeeper/${profile.shopkeeper_id || profile.id}`;
  };

  const wsUrl = getWebSocketUrl();

  const { isOpen: wsConnected } = useWebSocket(wsUrl, {
    onMessage: (message) => {
      // Type assertion - the message from WebSocket matches NotificationMessage structure
      handleWebSocketMessage(message as NotificationMessage);
    },
  });

  // Load initial data
  useEffect(() => {
    loadData();
  }, []);

  // Auto-scroll to document viewer when sender is selected (especially on mobile)
  useEffect(() => {
    if (selectedSender && documentViewerRef.current) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        documentViewerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 100);
    }
  }, [selectedSender]);

  // Periodic background sync (every 4s) to ensure real-time updates seamlessly
  useEffect(() => {
    const interval = setInterval(() => {
      loadDocumentsSilent();
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [profileRes, docsRes] = await Promise.all([
        shopkeepersAPI.getProfile(),
        documentsAPI.getDocuments()
      ]);
      setProfile(profileRes);
      // Filter out confirmed and deleted documents from active view
      const activeDocuments = docsRes.items.filter(
        (doc: Document) => doc.status !== 'confirmed' && doc.status !== 'deleted'
      );
      setDocuments(activeDocuments);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Failed to load dashboard data');
      }
      addToast('Failed to load dashboard data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadDocumentsSilent = async () => {
    try {
      const docsRes = await documentsAPI.getDocuments();
      const activeDocuments = docsRes.items.filter(
        (doc: Document) => doc.status !== 'confirmed' && doc.status !== 'deleted'
      );
      
      setDocuments(prev => {
        const prevIds = new Set(prev.map(d => d.id));
        const newArrivals = activeDocuments.filter(d => !prevIds.has(d.id));
        if (newArrivals.length > 0) {
          playNotificationSound();
          setNewDocId(newArrivals[0].id);
          setTimeout(() => setNewDocId(null), 3500);
          const firstNew = newArrivals[0];
          const sName = firstNew.sender?.name || 'Customer';
          addToast(`New document from ${sName}: ${firstNew.original_filename || 'document'}`, 'info', '📄');
        }
        return activeDocuments;
      });
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Failed to load documents silently:', error);
      }
    }
  };

  // Mark documents as read when sender is selected
  const handleSenderClick = async (senderKey: string) => {
    setSelectedSender(senderKey);
    // Note: We no longer automatically mark documents as "printed" just by viewing them.
    // The "printed" status should only be set when the shopkeeper actually prints the document.
    // Unread badges will remain until the status changes via user action.
  };

  // Add toast notification
  const addToast = (message: string, type: ToastProps['type'] = 'info', icon?: string) => {
    const id = toastIdRef.current++;
    setToasts(prev => [...prev, {
      id,
      message,
      type,
      icon,
      onClose: () => removeToast(id)
    }]);
  };

  const removeToast = (id: number) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Play notification sound
  const playNotificationSound = () => {
    try {
      // Create a simple notification beep using Web Audio API
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800; // Frequency in Hz
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      // Silently fail - notification sound is not critical
      if (import.meta.env.DEV) {
        console.error('Error playing notification sound');
      }
    }
  };

  // Handle WebSocket messages for real-time updates
  const handleWebSocketMessage = async (message: NotificationMessage) => {
    switch (message.type) {
      case 'new_document':
        if (message.data) {
          // Instead of re-fetching everything, we can either fetch just the new doc 
          // or rely on the data provided in the message if it's complete enough.
          // For now, let's fetch only if we don't have the full object.
          try {
            // To ensure we have the full sender relationship and metadata, 
            // we fetch the single new document
            const newDoc = await documentsAPI.getDocument(message.data.id);
            
            setDocuments(prev => {
              // Avoid duplicates
              if (prev.find(d => d.id === newDoc.id)) return prev;
              return [newDoc, ...prev];
            });

            // Highlight the new document with animation
            setNewDocId(message.data.id);
            setTimeout(() => setNewDocId(null), 3000);

            // Play notification sound
            playNotificationSound();

            // Show toast notification
            const senderName = message.data.sender_name || 'Anonymous';
            const filename = message.data.filename || 'document';
            addToast(
              `New document from ${senderName}: ${filename}`,
              'info',
              '📄'
            );

            // Show browser notification
            showNotification(`New document: ${filename} from ${senderName}`);
          } catch (error) {
            if (import.meta.env.DEV) {
              console.error('Failed to handle new document notification', error);
            }
            // Fallback: re-fetch all silently if single fetch fails
            loadDocumentsSilent();
          }
        }
        break;

      case 'document_confirmed':
        if (message.document_id) {
          setDocuments(prev =>
            prev.filter(doc => doc.id !== message.document_id)
          );
          addToast('Document confirmed and deleted', 'success', '✓');
        }
        break;

      case 'document_status_change':
        if (message.document_id && message.new_status) {
          setDocuments(prev =>
            prev.map(doc =>
              // Ensure we compare IDs correctly regardless of type (string vs number)
              String(doc.id) === String(message.document_id)
                ? { ...doc, status: message.new_status as any }
                : doc
            )
          );
          addToast(`Document status updated to ${message.new_status}`, 'info');
        }
        break;
    }
  };

  // Show browser notification
  const showNotification = (message: string) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Prindox', {
        body: message,
        icon: '/vite.svg',
        badge: '/vite.svg',
        tag: 'document-notification',
        requireInteraction: false
      });
    }
  };

  const shareShop = () => {
    if (!profile) return;
    const uploadUrl = `${window.location.origin}/send?shop=${profile.public_id}`;
    const message = `Hi! Now you can send documents for printing directly to my shop "${profile.shop_name}" via this link: ${uploadUrl}`;
    
    if (navigator.share) {
      navigator.share({
        title: `Print at ${profile.shop_name}`,
        text: message,
        url: uploadUrl
      }).catch(console.error);
    } else {
      const encodedMessage = encodeURIComponent(message);
      window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
    }
  };

  const handlePrintAll = async () => {
    if (!selectedSender || selectedSenderDocuments.length === 0) return;
    
    const unprintedDocs = selectedSenderDocuments.filter(doc => doc.status === 'uploaded');
    if (unprintedDocs.length === 0) {
      addToast('No new documents to print', 'info');
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: 'Print All Documents',
      message: `Do you want to open all ${unprintedDocs.length} new documents for printing? This will open them in separate tabs and mark them as printed.`,
      onConfirm: async () => {
        // Loop through and open each
        for (const doc of unprintedDocs) {
          const printWindow = window.open(doc.url, '_blank');
          if (printWindow) {
            // We can't easily trigger .print() across multiple popups reliably due to blockers
            // but we can at least open them.
          }
        }
        
        // Batch mark as printed
        const ids = unprintedDocs.map(d => String(d.id));
        try {
          await Promise.all(ids.map(id => documentsAPI.updateStatus(Number(id), 'printed')));
          setDocuments(prev => prev.map(doc => 
            ids.includes(String(doc.id)) ? { ...doc, status: 'printed' as any } : doc
          ));
          addToast(`Marked ${ids.length} documents as printed`, 'success');
        } catch (err) {
          addToast('Failed to update status for some documents', 'error');
        }
      }
    });
  };

  const handleQuickPrint = async (group: DocumentGroup) => {
    const unprintedDocs = group.documents.filter(doc => doc.status === 'uploaded');
    if (unprintedDocs.length === 0) {
        addToast('No new documents to print', 'info');
        return;
    }

    try {
        if (Capacitor.isNativePlatform()) {
          await Haptics.impact({ style: ImpactStyle.Medium });
        }
        await Promise.all(unprintedDocs.map(doc => documentsAPI.updateStatus(doc.id, 'printed')));
        loadData();
        addToast(`Marked ${unprintedDocs.length} documents as printed`, 'success');
    } catch (error) {
        addToast('Failed to update documents', 'error');
    }
  };

  const handleQuickArchive = async (group: DocumentGroup) => {
    try {
        if (Capacitor.isNativePlatform()) {
          await Haptics.notification({ type: NotificationType.Success });
        }
        await Promise.all(group.documents.map(doc => documentsAPI.updateStatus(doc.id, 'confirmed')));
        loadData();
        addToast(`Archived documents from ${getSenderName(group.sender)}`, 'success');
    } catch (error) {
        addToast('Failed to archive documents', 'error');
    }
  };

  // Request notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Helper functions (must be defined before useMemo hooks)
  const getSenderName = (sender: Sender) => sender.display_name || sender.effective_name || sender.provided_name || 'Anonymous';
  const getSenderEmail = (sender: Sender) => sender.effective_email || sender.provided_email;
  const getSenderPhone = (sender: Sender) => sender.effective_phone || sender.provided_phone;

  const getSenderKey = (sender: Sender) => {
    // Must match the grouping logic
    const email = getSenderEmail(sender);
    const phone = getSenderPhone(sender);

    if (email) {
      return `email:${email.toLowerCase().trim()}`;
    } else if (phone) {
      return `phone:${phone.trim()}`;
    } else {
      return `id:${sender.id}`;
    }
  };

  // Group documents by sender
  const documentGroups = useMemo<DocumentGroup[]>(() => {
    const groups: { [key: string]: DocumentGroup } = {};

    documents.forEach(doc => {
      if (!doc.sender) return; // Skip documents without sender

      const senderId = doc.sender.id;
      const senderEmail = getSenderEmail(doc.sender);
      const senderPhone = getSenderPhone(doc.sender);

      // Create a composite key for grouping - prioritize email > phone > id
      let senderKey: string;
      if (senderEmail) {
        senderKey = `email:${senderEmail.toLowerCase().trim()}`;
      } else if (senderPhone) {
        senderKey = `phone:${senderPhone.trim()}`;
      } else {
        senderKey = `id:${senderId}`;
      }

      if (!groups[senderKey]) {
        groups[senderKey] = {
          sender: doc.sender,
          documents: [],
          last_activity: doc.uploaded_at,
          unread_count: 0,
          total_documents: 0,
          total_cost: 0,
          is_urgent: false,
          latest_status: doc.status
        };
      }

      groups[senderKey].documents.push(doc);

      // Update total cost
      groups[senderKey].total_cost += (doc.estimated_cost || 0);
      
      // Update urgency
      if (doc.is_urgent) {
        groups[senderKey].is_urgent = true;
      }

      // Update last activity if this document is newer
      if (doc.uploaded_at > groups[senderKey].last_activity) {
        groups[senderKey].last_activity = doc.uploaded_at;
      }

      // Count unread (uploaded) documents
      if (doc.status === 'uploaded') {
        groups[senderKey].unread_count++;
      }

      // Update total documents count
      groups[senderKey].total_documents = groups[senderKey].documents.length;

      // Update latest status
      groups[senderKey].latest_status = doc.status;
    });

    // Convert to array and sort by last activity (newest first - like WhatsApp)
    return Object.values(groups)
      .sort((a, b) => new Date(b.last_activity).getTime() - new Date(a.last_activity).getTime());
  }, [documents]);

  // Filter groups based on search and filter
  const filteredGroups = useMemo(() => {
    return documentGroups.filter(group => {
      // Apply search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const senderName = getSenderName(group.sender);
        const senderEmail = getSenderEmail(group.sender);
        const senderPhone = getSenderPhone(group.sender);

        const matchesSender = senderName?.toLowerCase().includes(searchLower) ||
                             senderEmail?.toLowerCase().includes(searchLower) ||
                             senderPhone?.includes(searchTerm);
        const matchesDocument = group.documents.some(doc =>
          doc.original_filename.toLowerCase().includes(searchLower)
        );

        if (!matchesSender && !matchesDocument) {
          return false;
        }
      }

      // Apply status filter
      switch (filter) {
        case 'unread':
          return group.unread_count > 0;
        case 'printed':
          return group.documents.some(doc => doc.status === 'printed');
        case 'pending':
          return group.documents.some(doc => doc.status === 'uploaded');
        default:
          return true;
      }
    });
  }, [documentGroups, searchTerm, filter]);

  // Get documents for selected sender
  const selectedSenderDocuments = useMemo(() => {
    if (!selectedSender) return [];
    const group = documentGroups.find(g => getSenderKey(g.sender) === selectedSender);
    return group ? group.documents.sort((a, b) =>
      new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime()
    ) : [];
  }, [selectedSender, documentGroups]);

  // Auto-close viewer when selected sender has no remaining documents
  useEffect(() => {
    if (selectedSender && selectedSenderDocuments.length === 0) {
      setSelectedSender(null);
    }
  }, [selectedSender, selectedSenderDocuments.length]);

  // Get sender info for selected sender
  const selectedSenderInfo = useMemo(() => {
    if (!selectedSender) return { name: '', email: '' };
    const group = documentGroups.find(g => getSenderKey(g.sender) === selectedSender);
    if (!group) return { name: '', email: '' };
    return {
      name: getSenderName(group.sender),
      email: getSenderEmail(group.sender)
    };
  }, [selectedSender, documentGroups]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleStatusToggle = async (isOpen: boolean) => {
    try {
      await shopkeepersAPI.updateProfile({ is_open: isOpen });
      setProfile(prev => prev ? { ...prev, is_open: isOpen } : null);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Failed to update shop status');
      }
      addToast('Failed to update shop status', 'error');
    }
  };

  const formatTime = (timeString: string) => {
    const date = new Date(timeString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-100 flex flex-col overflow-hidden">
      {/* Header - WhatsApp Web Style */}
      <header className="bg-gray-100 px-4 py-2.5 flex-shrink-0">
        <div className="flex items-center justify-between">
          {/* Left side - Shop profile */}
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center flex-shrink-0 relative shadow-lg border border-slate-100">
              <img src="/logo-icon.svg" alt="Prindox Logo" className="w-8 h-8 object-contain" />
              {/* Connection status indicator */}
              {wsConnected && (
                <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white animate-pulse"></div>
              )}
            </div>
            <div className="min-w-0">
              <h1 className="text-base font-black text-gray-900 truncate tracking-tight">
                {profile?.shop_name || 'Dashboard'}
              </h1>
              <div className="flex items-center">
                <span className={`text-[10px] font-bold uppercase tracking-widest ${wsConnected ? 'text-green-500' : 'text-gray-400'}`}>
                  {wsConnected ? 'Live' : 'Connecting...'}
                </span>
              </div>
            </div>
          </div>
          {/* Right side - Controls (WhatsApp style icons) */}
          <div className="flex items-center space-x-2">
            <button
              onClick={shareShop}
              className="hidden sm:flex items-center space-x-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-bold shadow-sm"
              title="Share shop link on WhatsApp"
            >
              <ChatBubbleLeftRightIcon className="h-4 w-4" />
              <span>Share Shop</span>
            </button>

            <button
              onClick={() => {
                playNotificationSound();
                addToast('Notification sound tested!', 'success');
              }}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-full transition-colors"
              title="Test notification sound"
            >
              <SpeakerWaveIcon className="h-5 w-5" />
            </button>

            <OpenClosedToggle
              isOpen={profile?.is_open || false}
              onToggle={handleStatusToggle}
            />

            <Link
              to="/profile"
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-full transition-colors"
            >
              <Cog6ToothIcon className="h-5 w-5" />
            </Link>

            <button
              onClick={handleLogout}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-full transition-colors"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content - Two independent scrollable panels (WhatsApp Web Style) */}
      <div className="flex-1 flex overflow-hidden min-h-0 relative w-full">
        {/* Left Panel - Chats/Sender List (WhatsApp Web Style) */}
        <div className={`
          ${selectedSender ? 'hidden md:flex' : 'flex'}
          w-full md:w-[320px] lg:w-[380px] xl:w-[420px] md:flex-shrink-0
          bg-white border-r border-gray-200
          flex-col overflow-hidden h-full
        `}>
          {/* Search - Modern Clean */}
          <div className="bg-white px-3.5 py-2.5 border-b border-slate-100 flex-shrink-0">
            <div className="relative flex items-center">
              <MagnifyingGlassIcon className="h-4 w-4 absolute left-3.5 text-slate-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search customers or files..."
                className="w-full pl-10 pr-4 py-2 text-xs font-bold bg-slate-100 focus:bg-white border border-transparent focus:border-blue-500 rounded-xl outline-none transition-all placeholder:text-slate-400 text-slate-800"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Filters - Modern Pill Tabs */}
          <div className="flex items-center gap-1.5 p-2.5 bg-slate-50 border-b border-slate-100 flex-shrink-0 overflow-x-auto scrollbar-hide">
            {[
              { key: 'all', label: 'All' },
              { key: 'unread', label: 'Unread' },
              { key: 'pending', label: 'Pending' },
              { key: 'printed', label: 'Printed' },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setFilter(key as any)}
                className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-black uppercase tracking-wider transition-all ${
                  filter === key
                    ? 'bg-white text-blue-600 shadow-sm border border-slate-200/60'
                    : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Sender List - Independently Scrollable */}
          <div className="flex-1 overflow-y-auto min-h-0 bg-slate-50 divide-y divide-slate-100 scrollbar-hide">
            {filteredGroups.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-400 p-6 bg-white">
                <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center mb-3 text-blue-500">
                  <DocumentIcon className="h-7 w-7" />
                </div>
                <p className="text-sm font-black text-slate-800 tracking-tight">No documents yet</p>
                <p className="text-xs mt-1 text-center text-slate-500 mb-6 font-medium">Documents from customers will appear here in real time.</p>
                
                <button 
                  onClick={shareShop}
                  className="flex items-center space-x-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl transition-all shadow-md font-black text-xs uppercase tracking-wider"
                >
                  <ChatBubbleLeftRightIcon className="h-4 w-4" />
                  <span>Share My Shop Link</span>
                </button>
              </div>
            ) : (
              filteredGroups.map((group) => {
                const hasNewDoc = group.documents.some(doc => doc.id === newDocId);
                const senderKey = getSenderKey(group.sender);
                const isSelected = selectedSender === senderKey;
                return (
                  <div
                    key={senderKey}
                    onClick={() => handleSenderClick(senderKey)}
                    className={`
                      px-4 py-3.5 cursor-pointer transition-all border-l-4
                      ${isSelected ? 'bg-blue-50/80 border-blue-600 shadow-sm' : 'bg-white hover:bg-slate-50/80 border-transparent'}
                      ${hasNewDoc ? 'animate-pulse-once bg-blue-100/60' : ''}
                    `}
                  >
                    <div className="flex items-center space-x-3.5">
                      {/* Avatar */}
                      <div className="flex-shrink-0 relative">
                        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center text-base font-black shadow-sm ${
                          group.unread_count > 0 ? 'bg-blue-600 text-white shadow-blue-500/20' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {(getSenderName(group.sender) || getSenderEmail(group.sender) || 'A').charAt(0).toUpperCase()}
                        </div>
                        {group.unread_count > 0 && (
                          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-emerald-500 border-2 border-white rounded-full"></span>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {/* Top row: Name and Time */}
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center min-w-0">
                            <h3 className={`text-sm truncate ${
                              group.unread_count > 0 ? 'font-black text-slate-900' : 'font-bold text-slate-700'
                            }`}>
                              {getSenderName(group.sender)}
                            </h3>
                            {group.is_urgent && (
                              <span className="ml-2 flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-black bg-amber-100 text-amber-700 border border-amber-200 uppercase">
                                Urgent
                              </span>
                            )}
                          </div>
                          <span className={`text-[10px] font-bold whitespace-nowrap ml-2 ${
                            group.unread_count > 0 ? 'text-blue-600' : 'text-slate-400'
                          }`}>
                            {formatTime(group.last_activity)}
                          </span>
                        </div>

                        {/* Bottom row: Last document info and badge */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-1.5 min-w-0 flex-1">
                            <DocumentIcon className={`h-3.5 w-3.5 flex-shrink-0 ${group.unread_count > 0 ? 'text-blue-600' : 'text-slate-400'}`} />
                            <span className={`text-xs truncate font-medium ${
                              group.unread_count > 0 ? 'text-slate-900 font-bold' : 'text-slate-500'
                            }`}>
                              {group.documents.length === 1
                                ? group.documents[0].original_filename || 'Document'
                                : `${group.documents.length} documents`
                              }
                            </span>
                          </div>

                          {/* Unread badge */}
                          {group.unread_count > 0 && (
                            <span className="bg-blue-600 text-white text-[10px] font-black rounded-full px-2 py-0.5 min-w-[1.25rem] text-center shadow-sm ml-2">
                              {group.unread_count}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right Panel - Chat/Document View (Modern Clean Workspace) */}
        <AnimatePresence>
          {selectedSender && selectedSenderDocuments.length > 0 ? (
            <motion.div
              key="viewer"
              ref={documentViewerRef}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
              className={`
                fixed md:relative inset-0 md:inset-auto z-50 md:z-auto
                flex-1 flex flex-col overflow-hidden bg-slate-50
              `}
            >
              <div className="flex-1 flex flex-col h-full overflow-hidden">
                {/* Sender Header for the Document View */}
                <div className="bg-white px-5 py-3.5 flex items-center justify-between border-b border-slate-200 shadow-sm flex-shrink-0">
                  <div className="flex items-center space-x-3">
                    <button 
                      onClick={() => setSelectedSender(null)} 
                      className="md:hidden p-2 -ml-2 text-slate-600 hover:text-slate-900 rounded-xl hover:bg-slate-100 transition-all active:scale-95"
                      aria-label="Back"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" /></svg>
                    </button>
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center text-white font-black shadow-md shadow-blue-500/20">
                      {selectedSenderInfo.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h2 className="text-sm font-black text-slate-900 tracking-tight">{selectedSenderInfo.name}</h2>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{selectedSenderDocuments.length} {selectedSenderDocuments.length === 1 ? 'Document' : 'Documents'}</p>
                    </div>
                  </div>
                
                <div className="flex items-center space-x-2">
                  {selectedSenderDocuments.some(d => d.status === 'uploaded') && (
                    <button
                      onClick={handlePrintAll}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs font-bold shadow-sm"
                    >
                      <PrinterIcon className="h-4 w-4" />
                      <span>Print All</span>
                    </button>
                  )}
                  <button 
                    onClick={() => setSelectedSender(null)}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-full transition-colors hidden md:block"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-hidden">
                <DocumentViewer
                  documents={selectedSenderDocuments}
                  senderName={selectedSenderInfo.name}
                  senderEmail={selectedSenderInfo.email}
                  hideHeader={true} // We have our own header now
                  onStatusUpdate={async (documentId, status) => {
                try {
                  // Update backend
                  await documentsAPI.updateStatus(Number(documentId), status);

                  // Update local state immediately for instant UI update
                  setDocuments(prevDocs => {
                    // If confirmed or deleted, remove from list entirely
                    if (status === 'confirmed' || status === 'deleted') {
                      const newDocs = prevDocs.filter(doc => doc.id !== Number(documentId));
                      
                      // If this was the only document for the selected sender, deselect them
                      const remainingForSender = newDocs.filter(doc => {
                        if (!doc.sender) return false;
                        // getSenderKey logic replicated or use selectedSender
                        return true; // Simplified check, more robust below
                      });
                      
                      return newDocs;
                    }
                    
                    return prevDocs.map(doc =>
                      doc.id === Number(documentId) ? { ...doc, status } : doc
                    );
                  });
                } catch (error) {
                  if (import.meta.env.DEV) {
                    console.error('Failed to update document status');
                  }
                  addToast('Failed to update document status', 'error');
                  // If update fails, reload data to ensure consistency
                  await loadData();
                }
              }}
              onBatchStatusUpdate={async (documentIds, status) => {
                try {
                  // Perform batch update (in parallel for now, better if API supports batch)
                  await Promise.all(
                    documentIds.map(id => documentsAPI.updateStatus(Number(id), status))
                  );
                  
                  // Optimistic UI update
                  setDocuments(prevDocs => {
                    if (status === 'confirmed' || status === 'deleted') {
                        return prevDocs.filter(doc => !documentIds.includes(String(doc.id)));
                    }
                    return prevDocs.map(doc => 
                        documentIds.includes(String(doc.id)) ? { ...doc, status } : doc
                    );
                  });
                  
                  addToast(`Marked ${documentIds.length} documents as ${status}`, 'success', '✓');
                  
                  // Auto-close if all docs for current sender are gone
                  if (status === 'confirmed' || status === 'deleted') {
                      setSelectedSender(null);
                  }
                } catch (error) {
                   addToast('Failed to update some documents', 'error');
                   await loadData();
                }
              }}
              onClose={() => setSelectedSender(null)}
            />
          </div>
        </div>
      </motion.div>
    ) : (
        <div key="placeholder" className="hidden md:flex flex-1 flex-col items-center justify-start pt-8 md:pt-16 p-4 md:p-8 bg-gray-50 relative overflow-y-auto">
              {/* Background Decoration */}
              <div className="absolute top-0 left-0 w-full h-full opacity-5 pointer-events-none overflow-hidden">
                 <div className="absolute top-10 left-10 w-32 h-32 bg-blue-500 rounded-full blur-3xl"></div>
                 <div className="absolute bottom-10 right-10 w-40 h-40 bg-purple-500 rounded-full blur-3xl"></div>
              </div>

              <div className="text-center max-w-2xl w-full z-10 flex flex-col items-center">
                
                {/* Shop Branding & QR Code */}
                <div className="bg-white p-5 md:p-8 rounded-3xl shadow-xl border border-gray-100 w-full max-w-sm mb-6 transform transition-all hover:scale-[1.02] duration-300">
                  <div className="mb-4 text-center md:text-left">
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1 truncate px-2">
                      {profile?.shop_name || 'My Shop'}
                    </h2>
                    <p className="text-gray-500 text-xs truncate px-2">{profile?.full_name}</p>
                  </div>
                  
                  <div className="bg-gradient-to-tr from-gray-50 to-white p-3 md:p-4 rounded-2xl border-2 border-dashed border-blue-200 flex justify-center items-center mb-4 shadow-inner mx-auto w-48 md:w-64">
                    {profile?.public_id ? (
                      <div className="bg-white p-2 rounded-lg shadow-sm w-full relative">
                        <QRCode
                          value={`${window.location.origin}/send?shop=${profile.public_id}`}
                          size={256}
                          style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                          viewBox={`0 0 256 256`}
                        />
                        {/* Center Logo Icon Overlay */}
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="w-10 h-10 bg-white rounded-lg shadow-md p-1 border border-slate-100 flex items-center justify-center">
                            <img src="/logo-icon.svg" alt="Prindox Logo" className="w-7 h-7" />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="w-[150px] h-[150px] bg-gray-100 animate-pulse rounded-lg flex items-center justify-center text-gray-400">
                        <span className="text-[10px]">Loading QR...</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-center space-x-2 text-blue-600 font-bold text-xs">
                    <svg className="w-4 h-4 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="uppercase tracking-tight">Scan to Upload</span>
                  </div>
                </div>

                {/* Instructions Steps - Stacked on mobile */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6 mb-8 w-full max-w-md">
                    <div className="bg-white p-3 md:p-4 rounded-xl shadow-sm border border-gray-100 flex md:flex-col items-center text-left md:text-center space-x-4 md:space-x-0">
                        <div className="w-7 h-7 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-xs md:mb-3 flex-shrink-0">1</div>
                        <div>
                           <h3 className="font-bold text-gray-900 text-sm md:mb-1">Scan QR</h3>
                           <p className="text-[10px] text-gray-500 leading-tight">Customer scans code at counter</p>
                        </div>
                    </div>
                    <div className="bg-white p-3 md:p-4 rounded-xl shadow-sm border border-gray-100 flex md:flex-col items-center text-left md:text-center space-x-4 md:space-x-0">
                        <div className="w-7 h-7 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-xs md:mb-3 flex-shrink-0">2</div>
                        <div>
                           <h3 className="font-bold text-gray-900 text-sm md:mb-1">Upload</h3>
                           <p className="text-[10px] text-gray-500 leading-tight">They select and send their files</p>
                        </div>
                    </div>
                    <div className="bg-white p-3 md:p-4 rounded-xl shadow-sm border border-gray-100 flex md:flex-col items-center text-left md:text-center space-x-4 md:space-x-0">
                        <div className="w-7 h-7 bg-green-100 text-green-600 rounded-full flex items-center justify-center font-bold text-xs md:mb-3 flex-shrink-0">3</div>
                        <div>
                           <h3 className="font-bold text-gray-900 text-sm md:mb-1">Receive</h3>
                           <p className="text-[10px] text-gray-500 leading-tight">Documents appear here instantly</p>
                        </div>
                    </div>
                </div>

                {/* Stats summary - Pill Design Optimized for Mobile */}
                <div className="inline-flex bg-white rounded-full shadow-lg border border-gray-100 p-1 md:p-1.5 mb-4">
                  <div className="px-3 md:px-6 py-1.5 md:py-2 rounded-full hover:bg-gray-50 transition-colors">
                    <span className="block text-base md:text-xl font-black text-gray-800 leading-none text-center">{documents.length}</span>
                    <span className="text-[8px] md:text-[10px] font-black text-gray-400 uppercase tracking-widest">Total</span>
                  </div>
                  <div className="px-3 md:px-6 py-1.5 md:py-2 rounded-full hover:bg-orange-50 transition-colors border-x border-gray-50">
                    <span className="block text-base md:text-xl font-black text-orange-500 leading-none text-center">{documents.filter(d => d.status === 'uploaded').length}</span>
                    <span className="text-[8px] md:text-[10px] font-black text-orange-400 uppercase tracking-widest">New</span>
                  </div>
                  <div className="px-3 md:px-6 py-1.5 md:py-2 rounded-full hover:bg-green-50 transition-colors">
                    <span className="block text-base md:text-xl font-black text-green-600 leading-none text-center">{documents.filter(d => d.status === 'printed').length}</span>
                    <span className="text-[8px] md:text-[10px] font-black text-green-400 uppercase tracking-widest">Printed</span>
                  </div>
                </div>

                <div className="mt-8 text-gray-300 text-[10px] font-medium tracking-widest">
                  POWERED BY PRINDOX
                </div>
              </div>
            </div>
          )}
        </AnimatePresence>
      </div>

      {/* Custom Confirm Dialog Modal */}
      {confirmDialog && confirmDialog.isOpen && (
        <div className="fixed inset-0 bg-slate-900/60 z-[100] flex items-center justify-center p-4 backdrop-blur-md">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl border border-slate-50 animate-scaleIn">
            <h3 className="text-lg font-black text-slate-800 mb-2">{confirmDialog.title}</h3>
            <p className="text-xs text-slate-500 font-bold mb-6 leading-relaxed">{confirmDialog.message}</p>
            <div className="flex space-x-3">
              <button
                onClick={() => setConfirmDialog(null)}
                className="flex-grow py-3 px-4 bg-slate-50 text-slate-600 rounded-xl hover:bg-slate-100 transition-colors font-bold text-xs uppercase tracking-wider border border-slate-100"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  confirmDialog.onConfirm();
                  setConfirmDialog(null);
                }}
                className="flex-grow py-3 px-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-black text-xs uppercase tracking-widest shadow-lg shadow-blue-500/20 active:scale-95"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <div className="fixed top-4 right-4 z-[60] space-y-2">
        {toasts.map(toast => (
          <Toast key={toast.id} {...toast} />
        ))}
      </div>
    </div>
  );
};

export default ModernInboxDashboard;